import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { Store } from '@ngxs/store';
import { FlyToObject, SetActiveTool, SetLabelEditNode } from '../../../engine/state/engine.actions';
import { EngineState } from '../../../engine/state/engine.state';
import { EngineNodesState } from '../../../engine/state/nodes/engine.nodes.state';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { PaintPresetFade } from '../../../engine/models/paint-presets.const';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { ClipboardService } from '../../clipboard.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CameraService } from 'src/app/engine/camera.service';
import { UndoActions } from 'src/app/undo/state/undo.state.actions';
import { ApplyCameraPatch, SetCameraFlyTo } from 'src/app/engine/state/camera/engine.camera.actions';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { calcShownObjectState } from 'src/app/engine/state/nodes/node-reducers';
import { calcCameraFlyToState } from 'src/app/engine/state/camera/camera-reducers';
import { EngineService } from 'src/app/engine/engine.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { EngineColorRecipeModel } from 'src/app/edit/models/engine-color-recipe.model';
import { ClearCurrentRecipe, SetCurrentRecipe } from 'src/app/edit/state/paint/edit.paint.actions';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { SetView } from 'src/app/edit/state/edit.actions';
import { EditViewEnum } from 'src/app/edit/models/edit-view.enum';


@Component({
  selector: 'app-node-actions',
  templateUrl: './node-actions.component.html',
  styleUrls: ['./node-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NodeActionsComponent implements OnInit {
  application = EnvironmentApplication;
  shortId = '';

  @Input()
  public get node(): ViewTreeNode {
    return this._node;
  }
  public set node(value: ViewTreeNode) {
    this._node = value;
    
    const ids = this.node.id.split('-');
    this.shortId = (ids.length === 2) ? ids[1] : ids[0];
  }
  private _node: ViewTreeNode;

  @Input()
  theme: 'light' | 'dark' = 'dark';


  // Used for Google Analytics event tracking
  // See: https://www.notion.so/biodigital/Human-Web-Google-Analytics-Events-d685075c60b84e0fbca3c2139775608b
  @Input()
  eventCategory = '';

  @Output()
  nodeActionsClosed = new EventEmitter();

  @ViewChild('nodeActionsTrigger', { static: true }) 
  nodeActionsTrigger: MatMenuTrigger;

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private changeDetectorRef: ChangeDetectorRef,
    private clipboardService: ClipboardService,
    private snackbar: MatSnackBar,
    private cameraService: CameraService,
    private engineService: EngineService
  ) { }

  ngOnInit() {
  }

  private isNodeChecked(id: string) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getNodes);
    return nodes[id].checked;
  }

  private isNodeShown(id: string) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getNodes);
    return nodes[id].shown;
  }

  checkNode(node: ViewTreeNode, checked?: boolean) {
    const shouldCheck = checked === undefined ? !this.isNodeChecked(node.id) : checked;
    this.store.dispatch(new NodeActions.UpdateNodesChecked(shouldCheck, [node.id]));
  }

  checkInverse(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesChecked(true));
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false, [node.id]));
  }

  setCheckedNodes(node: ViewTreeNode, checked?: boolean) {
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));
    const shouldCheck = checked === undefined ? !this.isNodeChecked(node.id) : checked;
    this.store.dispatch(new NodeActions.UpdateNodesChecked(shouldCheck, [node.id]));
  }

  flyToNode(node: ViewTreeNode) {
    this.store.dispatch(new FlyToObject(node.id));
  }

  hideNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesShown(
      false,
      [node.id]
    ));
  }

  hideInverse(node: ViewTreeNode) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);

    const showNodeIds = this.graphService.getAllLeafDescendants(viewGraph, [node.id]);
    const hideNodeIds = this.graphService.getInverseSelection(viewGraph, [node.id]);

    const patch = {};

    showNodeIds.forEach((nodeId) => {
      patch[nodeId] = true;
    });

    hideNodeIds.forEach((nodeId) => {
      patch[nodeId] = false;
    });

    // calculate the next nodeState, and apply it via NodeActions.ApplyPatch
    const nodeAction = new NodeActions.UpdateNodesShown(undefined, undefined, patch);

    const nodeState = calcShownObjectState(
      this.store.selectSnapshot(EngineNodesState),
      nodeAction
    );

    this.store.dispatch(new NodeActions.ApplyPatch(nodeState.patches));
    

    // calculate the next camera location, and apply it via ApplyCameraPatch
    const objectBoundary = this.engineService.getObjectBoundary(node.id);
    const newCamera = this.cameraService.cameraAfterFlyTo(objectBoundary);
    const cameraAction = new SetCameraFlyTo(newCamera);
    const cameraState = calcCameraFlyToState(
      this.store.selectSnapshot(EngineCameraState),
      cameraAction
    );
    this.store.dispatch(new ApplyCameraPatch(cameraState.patches));

    // de-select object
    this.store.dispatch(new NodeActions.UpdateNodeSelected());
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));
    

    // save both sets of patches at the same do, so Undo and Redo act on them as a group
    this.store.dispatch(
      new UndoActions.SavePatches([
        {
          forward: nodeState.patches,
          inverse: nodeState.inversePatches,
          patchAction: NodeActions.ApplyPatch,
        },
        {
          forward: cameraState.patches,
          inverse: cameraState.inversePatches,
          patchAction: ApplyCameraPatch,
        },
      ])
    );
  }

  showNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesShown(
      true,
      [node.id]
    ));
    this.changeDetectorRef.markForCheck();
  }

  toggleVisible(node: ViewTreeNode, show?: boolean) {
    const shouldShow = show === undefined ? this.isNodeShown(node.id) : show;
    this.store.dispatch(new NodeActions.UpdateNodesShown(shouldShow, [node.id]));
    this.changeDetectorRef.markForCheck();
  }

  togglePickable(node: ViewTreeNode, pickable: boolean) {
    this.store.dispatch(new NodeActions.UpdateNodesPickable(pickable, [node.id]));
    this.changeDetectorRef.markForCheck();
  }

  makeEverythingElseUnpickale(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesPickable(false));
    this.store.dispatch(new NodeActions.UpdateNodesPickable(true, [node.id]));
  }

  fadeInverseNodes(node: ViewTreeNode) {
    // fade all nodes except this one
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const inverseNodeIds = this.graphService.getInverseSelection(viewGraph, [node.id]);
    const nodes = this.graphService.getNodes(viewGraph, inverseNodeIds);

    this.store.dispatch(new NodeActions.UpdateNodesPaint(
      PaintPresetFade.recipe,
      nodes.map(n => n.id)  
    ));

  }

  fadeNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesPaint(
      PaintPresetFade.recipe,
      [node.id]
    ));
  }

  editNodeLabel(node: ViewTreeNode) {
    this.store.dispatch(new SetLabelEditNode(node));
  }

  copy(value: string) {
    this.clipboardService.copyToClipboard(value, 'text').subscribe(() => {
      this.snackbar.open(`Copied ${value} to clipboard.`);
    });
  }

  onMenuClosed() {
    this.nodeActionsClosed.emit();
  }

  samplePaint(node: ViewTreeNode) {
    const colorrecipe = this.store.selectSnapshot(EngineNodesState.getNodePaint)(node.id);
    const recipe = ColorRecipeModel.toEngine(colorrecipe);
    EngineColorRecipeModel.isRealRecipe(recipe) ? this.store.dispatch(new SetCurrentRecipe(colorrecipe)) : this.store.dispatch(new ClearCurrentRecipe());
    this.store.dispatch(new SetActiveTool(ToolEnum.Paint));
    this.store.dispatch(new SetView(EditViewEnum.Paint));
  }

}
